import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getCategories, createCategory, updateCategory, deleteCategory, reorderCategories } from '../api/categories';

const PRESET_COLORS = [
  '#6366f1','#8b5cf6','#ec4899','#ef4444','#f97316',
  '#eab308','#22c55e','#10b981','#06b6d4','#3b82f6',
  '#64748b','#f1f5f9',
];

export default function CategoriesPage() {
  const qc                      = useQueryClient();
  const [modal, setModal]       = useState(null); // null | 'create' | category-object

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn:  () => getCategories().then((r) => r.data),
  });

  const reorderMut = useMutation({
    mutationFn: (ids) => reorderCategories(ids),
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['categories'] }),
  });

  function move(index, direction) {
    const ids    = categories.map((c) => c.id);
    const target = index + direction;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    reorderMut.mutate(ids);
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Categories</h1>
        <button className="btn-primary text-sm" onClick={() => setModal('create')}>
          + New category
        </button>
      </div>

      {isLoading ? (
        <Spinner />
      ) : categories.length === 0 ? (
        <div className="card p-12 text-center space-y-2">
          <p className="text-4xl">🏷️</p>
          <p className="text-gray-300 font-medium">No categories yet</p>
          <p className="text-gray-500 text-sm">Create categories to tag and filter your images.</p>
        </div>
      ) : (
        <div className="card divide-y divide-gray-800">
          {categories.map((cat, idx) => (
            <CategoryRow
              key={cat.id}
              category={cat}
              index={idx}
              total={categories.length}
              onEdit={() => setModal(cat)}
              onMove={move}
              onDeleted={() => qc.invalidateQueries({ queryKey: ['categories'] })}
            />
          ))}
        </div>
      )}

      {/* Create / edit modal */}
      {modal && (
        <CategoryModal
          initial={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['categories'] }); setModal(null); }}
        />
      )}
    </div>
  );
}

// ── Row ───────────────────────────────────────────────────────────────────────
function CategoryRow({ category, index, total, onEdit, onMove, onDeleted }) {
  const qc = useQueryClient();

  const deleteMut = useMutation({
    mutationFn: () => deleteCategory(category.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['images'] });
      onDeleted();
    },
  });

  return (
    <div className="px-4 py-3 flex items-center gap-3">
      {/* Color swatch */}
      <div
        className="w-4 h-4 rounded-full shrink-0 border border-black/20"
        style={{ background: category.color }}
      />

      <div className="flex-1 min-w-0">
        <Link
          to={`/dashboard?category_id=${category.id}`}
          className="font-medium text-white hover:text-brand-400 transition-colors truncate block"
        >
          {category.name}
          {category.is_default ? <span className="ml-2 text-xs text-gray-500">(default)</span> : null}
        </Link>
        <p className="text-xs text-gray-500">{category.image_count} image{category.image_count !== 1 ? 's' : ''}</p>
      </div>

      {/* Reorder */}
      <div className="flex flex-col gap-0.5">
        <button
          className="text-gray-600 hover:text-gray-300 text-xs leading-none transition-colors disabled:opacity-30"
          disabled={index === 0}
          onClick={() => onMove(index, -1)}
          title="Move up"
        >▲</button>
        <button
          className="text-gray-600 hover:text-gray-300 text-xs leading-none transition-colors disabled:opacity-30"
          disabled={index === total - 1}
          onClick={() => onMove(index, 1)}
          title="Move down"
        >▼</button>
      </div>

      <div className="flex gap-2 shrink-0">
        <button className="text-xs text-gray-400 hover:text-white transition-colors" onClick={onEdit}>
          Edit
        </button>
        <button
          className="text-xs text-red-400 hover:text-red-300 transition-colors"
          onClick={() => {
            if (confirm(`Delete category "${category.name}"? Images will be uncategorised.`)) {
              deleteMut.mutate();
            }
          }}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

// ── Create / edit modal ───────────────────────────────────────────────────────
function CategoryModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [name,       setName]       = useState(initial?.name       || '');
  const [color,      setColor]      = useState(initial?.color      || '#6366f1');
  const [isDefault,  setIsDefault]  = useState(!!initial?.is_default);
  const [err,        setErr]        = useState('');

  const mutation = useMutation({
    mutationFn: (data) =>
      isEdit ? updateCategory(initial.id, data) : createCategory(data),
    onSuccess: onSaved,
    onError:   (e) => setErr(e.response?.data?.error || 'Save failed.'),
  });

  function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim()) return setErr('Name is required.');
    mutation.mutate({ name: name.trim(), color, is_default: isDefault ? 1 : 0 });
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="card w-full max-w-sm p-6 space-y-4">
        <h3 className="font-semibold text-white">{isEdit ? 'Edit category' : 'New category'}</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Name</label>
            <input
              autoFocus
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Science, Finance, Health…"
            />
          </div>

          <div>
            <label className="label">Colour</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {PRESET_COLORS.map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => setColor(c)}
                  className={`w-7 h-7 rounded-full border-2 transition-transform ${
                    color === c ? 'border-white scale-110' : 'border-transparent'
                  }`}
                  style={{ background: c }}
                  title={c}
                />
              ))}
              {/* Custom colour */}
              <label
                className="w-7 h-7 rounded-full border-2 border-dashed border-gray-600 flex items-center justify-center cursor-pointer hover:border-gray-400 transition-colors text-gray-400 text-xs"
                title="Custom colour"
              >
                +
                <input
                  type="color"
                  className="sr-only"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                />
              </label>
            </div>
            <div
              className="mt-2 h-6 w-full rounded-md border border-gray-700"
              style={{ background: color }}
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              id="is_default"
              type="checkbox"
              className="w-4 h-4 rounded accent-brand-500"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
            />
            <label htmlFor="is_default" className="text-sm text-gray-300 cursor-pointer">
              Set as default category for new uploads
            </label>
          </div>

          {err && <p className="text-red-400 text-sm">{err}</p>}

          <div className="flex gap-3 pt-1">
            <button type="submit" className="btn-primary" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create'}
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center py-12">
      <div className="w-6 h-6 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}
